// Copyright (c) 2026, Dokos SAS and Contributors
// License: GNU General Public License v3. See license.txt

frappe.provide("erpnext.dokos");

// @dokos: the one façade every Dokos desk component draws with — v5-dev build.
//
// Same contract as `pe_ui` / `je_ui`, generalised: components call `erpnext.dokos.ui.*` and
// never `frappe.ui.<builder>` directly, so the component files stay BYTE-IDENTICAL across
// branches and only this file (plus `scss/dokos/_espresso.scss`) changes with the host
// framework. On Dodock `develop` the builders delegate to the Espresso component library
// (`frappe/public/js/frappe/ui/components/*`). THIS branch ships none of it — there is no
// `frappe/public/js/frappe/ui/components/` on v5-dev and `frappe.ui.badge` / `button` /
// `alert` / `empty_state` / `stepper` / `tab_buttons` / `progress` / `skeleton` are undefined —
// so the same entry points are hand-built here. They emit the SAME `es-*` markup (classes and
// data-attributes) as the develop builders, so component stylesheets and the raw `es-*` markup
// in Vue files / Jinja macros work unchanged; `_espresso.scss` carries the matching styles,
// written on the v5 Espresso tokens.
//
// Contract:
//   - every builder returns a jQuery object; builders with an `.html()` twin also return a
//     markup string for template literals (badge/pill, button, alert, skeleton, progress,
//     stat, timeline);
//   - `stepper`, `tab_buttons` and `progress` expose their instance on `$el.data(...)`
//     ("es-stepper", "es-tab-buttons", "es-progress") for set_current / set_value;
//   - `button` binds `onclick` and shows a busy state (aria-busy + spinner) while a returned
//     promise is pending;
//   - `status_theme()` is the single status → colour map (design page, "Status vocabulary").
//     Pills, bars and timeline dots all resolve through it.
//
// Icons go through `icon()` below: the v5 Lucide sprite predates a few renames, so the develop
// names are mapped in ICON_ALIASES, and every icon is drawn with `current_color` so it follows
// the text colour (an icon built without it is invisible on v5's dark pills).
//
// `TestDokosEspressoUI.test_facade_covers_every_call_site` asserts that every
// `erpnext.dokos.ui.<name>` used anywhere in the app is provided here.
//
// Design reference: https://claude.ai/artifact/WHZhFPAttov2VrpbXu9RwC

(() => {
	// Status (English doc value, case-insensitive) → theme. Unknown statuses fall back to gray.
	// gray  = not started / inactive / cancelled
	// blue  = running normally, informational
	// amber = waiting on someone, partial
	// green = done, balanced, covered
	// red   = needs action now
	const STATUS_THEMES = {
		gray: [
			"draft",
			"cancelled",
			"canceled",
			"stopped",
			"closed",
			"not started",
			"not used",
			"template",
			"disabled",
			"inactive",
			"draft invoices",
			"unknown",
		],
		blue: [
			"open",
			"in process",
			"in progress",
			"working",
			"started",
			"queued",
			"processing",
			"initiated",
			"material transferred",
			"work in progress",
			"not started yet",
			"trial",
			"submitted",
			"to deliver and bill",
			"to bill",
			"to deliver",
			"to receive and bill",
			"to receive",
			"prepared",
			"declared",
		],
		amber: [
			"requested",
			"pending",
			"pending review",
			"partially paid",
			"partly paid",
			"partially received",
			"partially delivered",
			"partially ordered",
			"partially reconciled",
			"unreconciled",
			"under-consumed",
			"under consumed",
			"upcoming",
			"billable",
			"payable",
			"cancelled and billable",
			"on hold",
			"hold",
			"unpaid",
			"past due date",
			"to verify",
		],
		green: [
			"paid",
			"completed",
			"complete",
			"reconciled",
			"match",
			"matched",
			"active",
			"accepted",
			"passed",
			"delivered",
			"received",
			"billed",
			"covered",
			"achieved",
			"balanced",
			"configured",
			"settled",
		],
		red: [
			"overdue",
			"failed",
			"rejected",
			"expired",
			"shortage",
			"missing",
			"over-consumed",
			"over consumed",
			"added",
			"added (not in bom)",
			"unbalanced",
			"error",
			"billing failed",
			"late",
			"blocked",
		],
	};

	const STATUS_INDEX = {};
	Object.entries(STATUS_THEMES).forEach(([theme, statuses]) =>
		statuses.forEach((status) => (STATUS_INDEX[status] = theme))
	);

	// Legacy indicator colours (frappe.ui.form indicator / indicator-pill) → Espresso theme.
	const INDICATOR_THEMES = {
		green: "green",
		blue: "blue",
		"light-blue": "blue",
		cyan: "blue",
		purple: "blue",
		violet: "blue",
		orange: "amber",
		yellow: "amber",
		amber: "amber",
		red: "red",
		pink: "red",
		gray: "gray",
		grey: "gray",
		darkgrey: "gray",
	};

	const THEMES = ["gray", "blue", "green", "amber", "red"];

	// ── v5 primitives ─────────────────────────────────────────────────────────────
	// Develop Lucide names → the v5 sprite's spelling (frappe/public/icons/lucide/icons.svg).
	const ICON_ALIASES = {
		"alert-triangle": "triangle-alert",
		"more-horizontal": "ellipsis",
		"more-vertical": "ellipsis-vertical",
		"rotate-ccw-clock": "history",
		"building-complex": "building-2",
	};

	const escape = (value) => frappe.utils.escape_html(value == null ? "" : String(value));

	function icon(name, size = "sm") {
		if (!name) return "";
		return frappe.utils.icon(ICON_ALIASES[name] || name, size, "", "", "", true);
	}

	// keep an option only when it is one of the allowed values (the develop builders warn and
	// drop unknown values the same way)
	const pick = (value, allowed) => (value != null && allowed.includes(value) ? value : null);

	// attrs → escaped `key="value"` strings; on* handlers and odd names are refused
	function safe_attrs(attrs) {
		const out = [];
		for (const [key, value] of Object.entries(attrs || {})) {
			if (value === false || value == null) continue;
			if (!/^[a-zA-Z][\w.:-]*$/.test(key) || /^on/i.test(key)) continue;
			out.push(value === true ? key : `${key}="${escape(value)}"`);
		}
		return out;
	}

	function safe_href(href) {
		if (!href) return null;
		const bare = String(href)
			.replace(/[\t\n\r]/g, "")
			.replace(/^[\u0000- ]+/, "");
		return /^(javascript|vbscript|data):/i.test(bare) ? null : href;
	}

	const CSS_LENGTH = /^[\d.]+(px|rem|em|%|vh|vw|ch)?$/;

	// badge — <span class="es-badge" data-theme data-size data-variant>
	const BADGE_THEMES = ["gray", "blue", "green", "amber", "red", "violet"];
	function badge_html(opts = {}) {
		const theme = pick(opts.theme === "orange" ? "amber" : opts.theme, BADGE_THEMES);
		const size = pick(opts.size, ["sm", "md", "lg"]);
		const variant = pick(opts.variant, ["solid", "subtle", "outline", "ghost"]);
		const icon_left = opts.icon_left || opts.icon;
		const attrs = [];
		if (theme && theme !== "gray") attrs.push(`data-theme="${theme}"`);
		if (size && size !== "md") attrs.push(`data-size="${size}"`);
		if (variant && variant !== "subtle") attrs.push(`data-variant="${variant}"`);
		if (opts.title) {
			attrs.push(`title="${escape(opts.title)}"`);
			if ((icon_left || opts.icon_right) && !opts.label) {
				attrs.push(`aria-label="${escape(opts.title)}"`);
			}
		}
		attrs.push(...safe_attrs(opts.attrs));
		const classes = escape(["es-badge", opts.css_class].filter(Boolean).join(" "));
		const label = opts.label
			? `<span class="${escape(
					["es-badge__label", opts.label_class].filter(Boolean).join(" ")
			  )}">${escape(opts.label)}</span>`
			: "";
		const affix = opts.icon_right ? `<span class="es-badge__affix">${icon(opts.icon_right)}</span>` : "";
		const attr_str = attrs.length ? " " + attrs.join(" ") : "";
		return `<span class="${classes}"${attr_str}>${icon(icon_left)}${label}${affix}</span>`;
	}

	// button — <button class="es-button" data-variant data-size data-theme>
	const BUTTON_VARIANTS = ["solid", "subtle", "outline", "ghost"];
	function default_loading_label(label) {
		if (!label) return null;
		const map = {
			[__("Save")]: __("Saving..."),
			[__("Delete")]: __("Deleting..."),
			[__("Submit")]: __("Submitting..."),
		};
		return map[label] || null;
	}

	function button_content_html(opts) {
		const loading_text = "loading_label" in opts ? opts.loading_label : default_loading_label(opts.label);
		const loading_label = loading_text
			? `<span class="es-button__loading-label" aria-live="polite">${escape(loading_text)}</span>`
			: "";
		const label = opts.label ? `<span class="es-button__label">${escape(opts.label)}</span>` : "";
		return `<span class="es-spinner" aria-hidden="true"></span>${loading_label}${icon(
			opts.icon_left || opts.icon
		)}${label}${icon(opts.icon_right)}`;
	}

	function button_html(opts = {}) {
		const variant = pick(opts.variant, BUTTON_VARIANTS);
		const size = pick(opts.size, ["xs", "sm", "md", "lg"]);
		const theme = pick(opts.theme, ["gray", "red"]);
		const icon_only = Boolean((opts.icon_left || opts.icon || opts.icon_right) && !opts.label);
		// no Espresso tooltip on v5: a tooltip option becomes the native title
		const tooltip = typeof opts.tooltip === "string" ? opts.tooltip : opts.tooltip?.text;
		const title = opts.title || tooltip;

		const attrs = [`type="${escape(opts.type || "button")}"`];
		if (variant && variant !== "subtle") attrs.push(`data-variant="${variant}"`);
		if (size && size !== "sm") attrs.push(`data-size="${size}"`);
		if (theme && theme !== "gray") attrs.push(`data-theme="${theme}"`);
		if (icon_only) attrs.push('data-icon-button="true"');
		if (opts.loading) attrs.push('aria-busy="true"');
		if (opts.disabled) attrs.push("disabled");
		if (title) attrs.push(`title="${escape(title)}"`);
		if (icon_only && title) attrs.push(`aria-label="${escape(title)}"`);
		attrs.push(...safe_attrs(opts.attrs));

		const classes = escape(["es-button", opts.css_class].filter(Boolean).join(" "));
		return `<button class="${classes}" ${attrs.join(" ")}>${button_content_html(opts)}</button>`;
	}

	function build_button(opts = {}) {
		const $btn = $(button_html(opts));
		if (opts.onclick) {
			$btn.on("click", function (e) {
				// keyboard activation still fires on a busy button — guard re-entry
				if ($btn.attr("aria-busy") === "true") return;
				const result = opts.onclick.call(this, e);
				if (result && typeof result.then === "function") {
					$btn.attr("aria-busy", "true");
					result.then(
						() => $btn.removeAttr("aria-busy"),
						(err) => {
							$btn.removeAttr("aria-busy");
							if (frappe.boot?.developer_mode) {
								console.error("erpnext.dokos.ui.button: onclick rejected", err);
							}
						}
					);
				}
			});
		}
		return $btn;
	}

	// alert — <div class="es-alert" role="alert" data-theme="yellow|blue|red|green">
	const ALERT_ICONS = {
		yellow: "triangle-alert",
		blue: "info",
		red: "circle-x",
		green: "circle-check",
	};
	function base_alert_html(opts = {}) {
		const theme = pick(opts.theme === "amber" ? "yellow" : opts.theme, Object.keys(ALERT_ICONS));
		const variant = pick(opts.variant, ["subtle", "outline"]);
		const attrs = ['role="alert"'];
		if (theme) attrs.push(`data-theme="${theme}"`);
		if (variant && variant !== "subtle") attrs.push(`data-variant="${variant}"`);
		attrs.push(...safe_attrs(opts.attrs));
		const description = opts.description
			? `<p class="es-alert__description">${escape(opts.description)}</p>`
			: "";
		const dismiss = opts.dismissible
			? `<button class="es-alert__dismiss" type="button" aria-label="${escape(__("Dismiss"))}">${icon(
					"x"
			  )}</button>`
			: "";
		const classes = escape(["es-alert", opts.css_class].filter(Boolean).join(" "));
		return `<div class="${classes}" ${attrs.join(" ")}>${icon(
			opts.icon || (theme && ALERT_ICONS[theme])
		)}<div class="es-alert__content"><span class="es-alert__title">${escape(
			opts.title || ""
		)}</span>${description}</div>${dismiss}</div>`;
	}

	function base_alert(opts = {}) {
		const $el = $(base_alert_html(opts));
		if (opts.dismissible) {
			$el.find(".es-alert__dismiss").on("click", () => {
				$el.remove();
				opts.on_dismiss && opts.on_dismiss();
			});
		}
		if (opts.footer) {
			const footer = typeof opts.footer === "function" ? opts.footer() : opts.footer;
			$('<div class="es-alert__footer"></div>').append(footer).appendTo($el);
		}
		return $el;
	}

	// progress — <div class="es-progress"><div class="es-progress__track" role="progressbar">
	let progress_id = 0;
	class Progress {
		constructor(opts = {}) {
			this.opts = opts;
			this.size = pick(opts.size, ["sm", "md", "lg", "xl"]) || "sm";
			this.intervals = Boolean(opts.intervals);
			this.interval_count = opts.interval_count || 6;

			this.el = document.createElement("div");
			this.el.className = ["es-progress", opts.css_class].filter(Boolean).join(" ");

			if (opts.label || opts.hint) {
				const header = document.createElement("div");
				header.className = "es-progress__header";
				if (opts.label) {
					this.label_el = document.createElement("span");
					this.label_el.className = "es-progress__label";
					this.label_el.id = `es-progress-label-${++progress_id}`;
					this.label_el.textContent = opts.label;
					header.appendChild(this.label_el);
				}
				if (opts.hint) {
					this.hint_el = document.createElement("span");
					this.hint_el.className = "es-progress__hint";
					header.appendChild(this.hint_el);
				}
				this.el.appendChild(header);
			}

			this.track = document.createElement("div");
			this.track.className = "es-progress__track";
			this.track.setAttribute("role", "progressbar");
			this.track.setAttribute("aria-valuemin", "0");
			this.track.setAttribute("aria-valuemax", "100");
			if (this.label_el) this.track.setAttribute("aria-labelledby", this.label_el.id);
			if (this.size !== "sm") this.track.setAttribute("data-size", this.size);
			this.el.appendChild(this.track);

			if (this.intervals) {
				this.track.setAttribute("data-intervals", "");
				this.segments = [];
				for (let i = 0; i < this.interval_count; i++) {
					const segment = document.createElement("div");
					segment.className = "es-progress__segment";
					this.segments.push(segment);
					this.track.appendChild(segment);
				}
			} else {
				this.fill = document.createElement("div");
				this.fill.className = "es-progress__fill";
				this.track.appendChild(this.fill);
			}

			this.set_value(opts.value || 0);
			this.$el = $(this.el);
			this.$el.data("es-progress", this);
		}

		set_value(value) {
			this.value = value;
			const shown = Math.min(Math.max(flt(value), 0), 100);
			this.track.setAttribute("aria-valuenow", String(shown));
			if (this.intervals) {
				const filled = Math.round((shown / 100) * this.interval_count);
				this.segments.forEach((segment, i) => segment.toggleAttribute("data-filled", i < filled));
			} else {
				this.fill.style.width = `${shown}%`;
			}
			if (this.hint_el) {
				this.hint_el.textContent =
					typeof this.opts.hint === "function" ? this.opts.hint(value) : `${Math.round(shown)}%`;
			}
		}

		get_value() {
			return this.value;
		}
	}

	// stepper — <nav class="es-stepper"><button class="es-stepper__step" data-state>
	class Stepper {
		constructor(opts = {}) {
			this.steps = opts.steps || [];
			this.current = opts.current || 0;
			this.is_locked = opts.is_locked || null;
			this.is_completed = opts.is_completed || null;
			this.on_step_click = opts.on_step_click || null;
			this.on_locked_click = opts.on_locked_click || null;
			this.compact = Boolean(opts.compact);

			this.nav = document.createElement("nav");
			this.nav.className = ["es-stepper", opts.css_class].filter(Boolean).join(" ");
			this.nav.setAttribute("aria-label", opts.label || __("Steps"));
			this.$el = $(this.nav);
			this.$el.data("es-stepper", this);
			this.render();
		}

		set_current(index) {
			this.current = Math.max(0, Math.min(index, this.steps.length - 1));
			this.render();
		}

		next_step() {
			this.set_current(this.current + 1);
		}

		prev_step() {
			this.set_current(this.current - 1);
		}

		refresh() {
			this.render();
		}

		done(index) {
			return this.is_completed ? Boolean(this.is_completed(index)) : index < this.current;
		}

		locked(index) {
			return Boolean(this.is_locked && index !== this.current && this.is_locked(index));
		}

		render() {
			// owners re-render often: skip the rebuild when nothing changed
			const key = [
				this.compact ? "compact" : "full",
				this.current,
				...this.steps.map(
					(step, i) => `${step.label}:${Number(this.done(i))}:${Number(this.locked(i))}`
				),
			].join("|");
			if (key === this._render_key) return;
			this._render_key = key;

			// a rebuild drops focus: restore it to the same step for keyboard users
			const focused = document.activeElement?.closest?.(".es-stepper__step");
			const had_focus =
				focused && this.nav.contains(focused)
					? Array.from(this.nav.querySelectorAll(".es-stepper__step")).indexOf(focused)
					: -1;

			this.nav.textContent = "";
			if (this.compact) {
				const count = this.steps.length;
				const position = this.current + 1;
				this.nav.appendChild(
					new Progress({
						label: this.steps[this.current]?.label || "",
						hint: () => __("Step {0} of {1}", [position, count]),
						intervals: true,
						interval_count: count,
						size: "md",
						value: count ? (position / count) * 100 : 0,
					}).el
				);
				return;
			}

			this.steps.forEach((step, index) => {
				if (index > 0) {
					const connector = document.createElement("span");
					connector.className = "es-stepper__connector";
					connector.setAttribute("aria-hidden", "true");
					if (this.done(index - 1)) connector.setAttribute("data-completed", "true");
					this.nav.appendChild(connector);
				}

				const locked = this.locked(index);
				const is_done = this.done(index);
				const state =
					index === this.current ? "active" : is_done ? "completed" : locked ? "locked" : null;

				const button = document.createElement("button");
				button.type = "button";
				button.className = "es-stepper__step";
				if (state) button.setAttribute("data-state", state);
				if (state === "active") button.setAttribute("aria-current", "step");
				if (is_done) button.setAttribute("data-completed", "true");
				if (locked) button.setAttribute("aria-disabled", "true");

				const marker = document.createElement("span");
				marker.className = "es-stepper__marker";
				marker.innerHTML = icon(
					is_done
						? state === "active"
							? "dot"
							: "check"
						: state === "active"
						? "circle-dot-dashed"
						: "circle-dashed"
				);
				button.appendChild(marker);

				const label = document.createElement("span");
				label.className = "es-stepper__label";
				label.textContent = step.label;
				button.appendChild(label);

				button.addEventListener("click", () => {
					if (button.getAttribute("aria-disabled") === "true") {
						this.on_locked_click && this.on_locked_click(index);
						return;
					}
					if (index === this.current) return;
					this.on_step_click && this.on_step_click(index);
				});
				this.nav.appendChild(button);
			});

			if (had_focus > -1) {
				const target = this.nav.querySelectorAll(".es-stepper__step")[had_focus];
				target && target.focus({ preventScroll: true });
			}
		}
	}

	// tab buttons — <div class="es-tab-buttons" role="radiogroup"><button class="es-pill" role="radio">
	class TabButtons {
		constructor(opts = {}) {
			this.opts = opts;
			this.options = opts.options || [];
			this.type = pick(opts.type, ["subtle", "ghost", "underline", "browser-tab"]) || "subtle";
			this.size = pick(opts.size, ["sm", "md"]) || "sm";
			this.direction = pick(opts.direction, ["left", "right"]) || "left";
			this.vertical = Boolean(opts.vertical);

			this.el = document.createElement("div");
			this.el.className = ["es-tab-buttons", opts.css_class].filter(Boolean).join(" ");
			this.el.setAttribute("role", "radiogroup");
			if (opts.label) this.el.setAttribute("aria-label", opts.label);
			if (this.type !== "subtle") this.el.setAttribute("data-type", this.type);
			if (this.size !== "sm") this.el.setAttribute("data-size", this.size);
			if (this.vertical) {
				this.el.setAttribute("data-orientation", "vertical");
				if (this.type === "browser-tab" && this.direction === "right") {
					this.el.setAttribute("data-direction", "right");
				}
			}

			this.pills = this.options.map((option) => this.build_pill(option));
			this.pills.forEach((pill) => this.el.appendChild(pill.el));

			const initial =
				("value" in opts ? this.find_by_value(opts.value) : null) ||
				this.pills.find((pill) => pill.option.active && !pill.option.disabled) ||
				this.pills.find((pill) => !pill.option.disabled);
			this.select(initial, { silent: true });

			// radio-group keyboard pattern: arrows move AND select, wrapping, skipping disabled
			this.el.addEventListener("keydown", (e) => {
				const rtl = frappe.utils.is_rtl ? frappe.utils.is_rtl() : false;
				const forward = rtl ? "ArrowLeft" : "ArrowRight";
				const backward = rtl ? "ArrowRight" : "ArrowLeft";
				const step =
					e.key === forward || e.key === "ArrowDown"
						? 1
						: e.key === backward || e.key === "ArrowUp"
						? -1
						: 0;
				if (!step) return;
				e.preventDefault();
				const enabled = this.pills.filter((pill) => !pill.option.disabled);
				if (!enabled.length) return;
				const current = Math.max(enabled.indexOf(this.selected), 0);
				const next = enabled[(current + step + enabled.length) % enabled.length];
				this.select(next);
				next.el.focus();
			});

			this.$el = $(this.el);
			this.$el.data("es-tab-buttons", this);
		}

		resolve_extra(extra, option) {
			if (typeof extra === "function") extra = extra(option);
			return (extra && $(extra)[0]) || null;
		}

		build_pill(option) {
			const el = document.createElement("button");
			el.type = "button";
			if (option.disabled) el.disabled = true;
			el.className = ["es-pill", option.css_class].filter(Boolean).join(" ");
			el.setAttribute("role", "radio");
			if (this.type === "underline") el.setAttribute("data-variant", "underline");
			if (this.type === "browser-tab") {
				el.setAttribute("data-variant", "browser-tab");
				if (!this.vertical) el.setAttribute("data-tab-base", "default");
			}
			if (this.type === "ghost") el.setAttribute("data-active-style", "subtle");
			if (this.size !== "sm") el.setAttribute("data-size", this.size);

			const prefix = this.resolve_extra(option.prefix, option);
			prefix && el.appendChild(prefix);

			el.insertAdjacentHTML("beforeend", icon(option.icon || option.icon_left));
			if (option.icon) {
				// icon-only: square, the label becomes the accessible name
				el.setAttribute("data-icon-only", "");
				if (option.label) el.setAttribute("aria-label", option.label);
			} else if (option.label) {
				el.appendChild(document.createTextNode(option.label));
			}
			if (option.icon_right && !option.icon) {
				el.insertAdjacentHTML("beforeend", icon(option.icon_right));
			}

			const suffix = this.resolve_extra(option.suffix, option);
			suffix && el.appendChild(suffix);

			if (option.title) el.title = option.title;

			const pill = { el, option };
			el.addEventListener("click", (e) => {
				this.select(pill);
				option.onclick && option.onclick(e);
			});
			return pill;
		}

		find_by_value(value) {
			return this.pills.find((pill) => Object.is(this.value_of(pill), value));
		}

		value_of(pill) {
			return "value" in pill.option ? pill.option.value : pill.option.label;
		}

		select(pill, { silent = false } = {}) {
			if (!pill || pill === this.selected) return;
			this.selected = pill;
			for (const entry of this.pills) {
				const active = entry === pill;
				entry.el.setAttribute("data-state", active ? "active" : "inactive");
				entry.el.setAttribute("aria-checked", active ? "true" : "false");
				entry.el.tabIndex = active ? 0 : -1;
				if (this.type === "browser-tab" && this.vertical) {
					if (active) {
						entry.el.setAttribute("data-tab-base", this.direction);
					} else {
						entry.el.removeAttribute("data-tab-base");
					}
				}
			}
			if (!silent) {
				this.opts.on_change && this.opts.on_change(this.value_of(pill), pill.option);
			}
		}

		get_value() {
			return this.selected ? this.value_of(this.selected) : null;
		}

		set_value(value, { silent = false } = {}) {
			const pill = this.find_by_value(value);
			if (pill) this.select(pill, { silent });
		}
	}

	// empty state — develop composes it from utility classes v5 does not ship; the same
	// layout is drawn here with `es-empty-state*` classes (styled in _espresso.scss)
	function build_empty_action(action) {
		if (!action || !action.label) return null;
		if (action.href) {
			const variant = pick(action.variant, BUTTON_VARIANTS) || "ghost";
			const href = safe_href(action.href);
			const attrs = [];
			if (variant !== "subtle") attrs.push(`data-variant="${variant}"`);
			if (action.theme === "red") attrs.push('data-theme="red"');
			if (href) attrs.push(`href="${escape(href)}" target="_blank" rel="noreferrer noopener"`);
			const classes = escape(["es-button", action.css_class].filter(Boolean).join(" "));
			return $(
				`<a class="${classes}" ${attrs.join(" ")}>${icon(
					action.icon
				)}<span class="es-button__label">${escape(action.label)}</span></a>`
			)[0];
		}
		return build_button({
			label: action.label,
			variant: action.variant,
			theme: action.theme,
			icon: action.icon,
			size: action.size,
			css_class: action.css_class,
			onclick: action.onclick,
		})[0];
	}

	function base_empty_state(opts = {}) {
		const $el = $("<div></div>").addClass(["es-empty-state", opts.css_class].filter(Boolean).join(" "));
		if (opts.icon) {
			$(`<div class="es-empty-state__icon">${icon(opts.icon, "md")}</div>`).appendTo($el);
		}
		const $text = $('<div class="es-empty-state__text"></div>').appendTo($el);
		$('<div class="es-empty-state__title"></div>')
			.text(opts.title || "")
			.appendTo($text);
		if (opts.description) {
			$('<div class="es-empty-state__description"></div>').text(opts.description).appendTo($text);
		}
		const actions = (opts.actions || []).map(build_empty_action).filter(Boolean);
		if (actions.length) {
			$('<div class="es-empty-state__actions"></div>').append(actions).appendTo($el);
		}
		return $el;
	}

	// skeleton — <div class="es-skeleton" aria-hidden="true">
	function skeleton_html(opts = {}) {
		const attrs = ['aria-hidden="true"'];
		const length = (value) => (value != null && CSS_LENGTH.test(value) ? value : "");
		const style = [
			length(opts.width) ? `width: ${opts.width}` : "",
			length(opts.height) ? `height: ${opts.height}` : "",
		]
			.filter(Boolean)
			.join("; ");
		if (style) attrs.push(`style="${style}"`);
		attrs.push(...safe_attrs(opts.attrs));
		const classes = escape(["es-skeleton", opts.css_class].filter(Boolean).join(" "));
		return `<div class="${classes}" ${attrs.join(" ")}></div>`;
	}

	// ── façade (same body as develop from here on, on the v5 primitives above) ────────
	function status_theme(status, fallback = "gray") {
		if (!status) return fallback;
		return STATUS_INDEX[String(status).trim().toLowerCase()] || fallback;
	}

	// Theme → legacy indicator colour, for APIs that still take one (frm.page.set_indicator,
	// list get_indicator, set_indicator_formatter). "amber" is spelled "orange" there.
	const INDICATOR_COLORS = { gray: "gray", blue: "blue", green: "green", amber: "orange", red: "red" };
	function indicator_color(status_or_theme) {
		const theme = THEMES.includes(status_or_theme) ? status_or_theme : status_theme(status_or_theme);
		return INDICATOR_COLORS[theme] || "gray";
	}

	function indicator_theme(color) {
		return INDICATOR_THEMES[String(color || "").toLowerCase()] || "gray";
	}

	// Resolve opts.theme / opts.status / opts.indicator to one of THEMES.
	function resolve_theme(opts = {}) {
		if (opts.theme && THEMES.includes(opts.theme)) return opts.theme;
		if (opts.indicator) return indicator_theme(opts.indicator);
		if (opts.status) return status_theme(opts.status);
		return "gray";
	}

	// A status pill. `label` defaults to the translated status.
	// opts: { label?, status?, theme?, indicator?, size?, icon?, title?, css_class? }
	function pill_opts(opts = {}) {
		return {
			label: opts.label ?? (opts.status ? __(opts.status) : ""),
			theme: resolve_theme(opts),
			variant: opts.variant || "subtle",
			size: opts.size || "sm",
			icon: opts.icon,
			title: opts.title,
			css_class: opts.css_class,
			attrs: opts.attrs,
		};
	}

	// No label and no icon → nothing at all, rather than an empty grey capsule (rows whose
	// status field is blank).
	const is_empty = (o) => !o.label && !o.icon;
	function pill(opts) {
		const o = pill_opts(opts);
		return is_empty(o) ? $() : $(badge_html(o));
	}
	pill.html = (opts) => {
		const o = pill_opts(opts);
		return is_empty(o) ? "" : badge_html(o);
	};

	// Banner. `theme` follows the pill vocabulary ("amber" for warnings).
	function alert_opts(opts = {}) {
		const theme = opts.status || opts.indicator ? resolve_theme(opts) : opts.theme;
		return { ...opts, theme: theme === "gray" ? undefined : theme };
	}
	function alert(opts) {
		return base_alert(alert_opts(opts));
	}
	alert.html = (opts) => base_alert_html(alert_opts(opts));

	// Progress bar coloured through the status map: { value, theme?|status?, size?, label?, hint? }
	function progress(opts = {}) {
		const tone = resolve_theme(opts);
		const $el = new Progress({ ...opts, theme: undefined }).$el;
		$el.addClass("dk-progress").attr("data-tone", tone);
		return $el;
	}
	progress.html = (opts = {}) => {
		const tone = resolve_theme(opts);
		const value = Math.min(Math.max(flt(opts.value), 0), 100);
		const size = opts.size && opts.size !== "sm" ? ` data-size="${opts.size}"` : "";
		const label = opts.label ? ` aria-label="${frappe.utils.escape_html(opts.label)}"` : "";
		return `<div class="es-progress dk-progress ${opts.css_class || ""}" data-tone="${tone}">
			<div class="es-progress__track"${size} role="progressbar" aria-valuemin="0"
				aria-valuemax="100" aria-valuenow="${value}"${label}>
				<div class="es-progress__fill" style="width: ${value}%"></div>
			</div>
		</div>`;
	};

	function stepper(opts) {
		return new Stepper(opts).$el;
	}

	function tab_buttons(opts) {
		return new TabButtons(opts).$el;
	}

	function button(opts) {
		return build_button(opts);
	}
	button.html = (opts) => button_html(opts);

	// A link that looks like a button: navigation stays a real <a> (middle-click, copy link,
	// open in new tab) instead of a <button> calling window.open.
	// { label, href, icon?, icon_right?, variant?, title?, css_class?, new_tab?, attrs? }
	function link_button_html(opts = {}) {
		const attrs = [`href="${escape(safe_href(opts.href) || "#")}"`];
		if (opts.variant && opts.variant !== "subtle") attrs.push(`data-variant="${opts.variant}"`);
		if (opts.title) attrs.push(`title="${escape(opts.title)}"`);
		if (opts.new_tab) attrs.push('target="_blank" rel="noopener noreferrer"');
		attrs.push(...safe_attrs(opts.attrs));
		const classes = escape(["es-button", opts.css_class].filter(Boolean).join(" "));
		const label = opts.label ? `<span class="es-button__label">${escape(opts.label)}</span>` : "";
		return `<a class="${classes}" ${attrs.join(" ")}>${icon(opts.icon)}${label}${icon(
			opts.icon_right
		)}</a>`;
	}
	function link_button(opts) {
		return $(link_button_html(opts));
	}
	link_button.html = link_button_html;

	// Money in a currency's own number-format precision. Plain format_currency falls back to
	// float_precision (3) when currency_precision is unset, which printed "775 609,875 €".
	function format_money(value, currency) {
		const decimals = get_number_format_info(get_number_format(currency)).precision;
		return format_currency(value, currency, decimals);
	}

	function format_percent(value, decimals = 2) {
		return `${format_number(value, null, decimals)} %`;
	}

	function empty_state(opts) {
		return base_empty_state(opts);
	}

	function skeleton(opts) {
		return $(skeleton_html(opts));
	}
	skeleton.html = (opts) => skeleton_html(opts);

	// KPI tile: { label, value (pre-formatted HTML from format_currency etc.), delta?, tone? }
	// `tone` colours the VALUE ("red" for a negative margin); there is no decorative stripe.
	function stat_html({ label, value, delta, tone } = {}) {
		const tone_attr = tone && tone !== "gray" ? ` data-tone="${tone}"` : "";
		return `<div class="dk-stat">
			<span class="dk-stat__label">${frappe.utils.escape_html(label || "")}</span>
			<span class="dk-stat__value"${tone_attr}>${value ?? "—"}</span>
			${delta ? `<span class="dk-stat__delta">${delta}</span>` : ""}
		</div>`;
	}
	function stat(opts) {
		return $(stat_html(opts));
	}
	stat.html = stat_html;

	function stat_grid(items = []) {
		return $(`<div class="dk-stat-grid">${items.map(stat_html).join("")}</div>`);
	}

	// Timeline: [{ label (HTML), time?, status?|theme? }]
	function timeline_html(items = []) {
		return `<div class="dk-timeline">${items
			.map((item) => {
				const tone = resolve_theme(item);
				const tone_attr = tone !== "gray" ? ` data-tone="${tone}"` : "";
				return `<div class="dk-tl">
					<span class="dk-tl__dot"${tone_attr}></span>
					<span>${item.label || ""}</span>
					<span class="dk-tl__time">${item.time || ""}</span>
				</div>`;
			})
			.join("")}</div>`;
	}
	function timeline(items) {
		return $(timeline_html(items));
	}
	timeline.html = timeline_html;

	// Section header: { title, meta?, actions? (jQuery|Element|string) }
	function section_header({ title, meta, actions } = {}) {
		const $h = $(`<div class="dk-sh">
			<span class="dk-sh__title">${frappe.utils.escape_html(title || "")}</span>
			${meta ? `<span class="dk-sh__meta">${frappe.utils.escape_html(meta)}</span>` : ""}
			<span class="dk-sh__actions"></span>
		</div>`);
		if (actions) $h.find(".dk-sh__actions").append(actions);
		return $h;
	}

	// Slide-in panel scaffold for dodock_drawer content: returns the parts to fill.
	// { title, on_close? } → { $el, $header, $title, $body, $footer }
	function panel({ title, on_close } = {}) {
		const $el = $(`<div class="dk-panel">
			<div class="dk-panel__header">
				<span class="dk-sh__title dk-panel__title"></span>
			</div>
			<div class="dk-panel__body"></div>
			<div class="dk-panel__footer"></div>
		</div>`);
		$el.find(".dk-panel__title").text(title || "");
		if (on_close) {
			$el.find(".dk-panel__header").append(
				button({
					icon: "x",
					variant: "ghost",
					title: __("Close"),
					css_class: "ml-auto",
					onclick: on_close,
				})
			);
		}
		return {
			$el,
			$header: $el.find(".dk-panel__header"),
			$title: $el.find(".dk-panel__title"),
			$body: $el.find(".dk-panel__body"),
			$footer: $el.find(".dk-panel__footer"),
		};
	}

	erpnext.dokos.ui = {
		status_theme,
		indicator_theme,
		indicator_color,
		pill,
		badge: pill,
		alert,
		progress,
		stepper,
		tab_buttons,
		button,
		link_button,
		format_money,
		format_percent,
		empty_state,
		skeleton,
		stat,
		stat_grid,
		timeline,
		section_header,
		panel,
	};
})();
